/**
 * Sheet 23, report a bureau rejection, a §7.13 modal over Results (mission 6
 * WP10 T053, T054; FR-021, FR-023, FR-027, NFR-007; US10; ANA.11, T.3, X.1,
 * X.9, X.10; C-013; DM-01M39PR5FSZDGG214WPRBW9ZXD; research §1.22; post-plan
 * C11; post-tasks R7, E2).
 *
 * The dispatcher renders this only for a `ready` run (RunPage, C11), so the
 * screen gates on the path alone: a Path B run renders the X.10 band, built
 * without a request, and no form, because Trellis did not generate the file.
 * A Path A run renders the form, then the confirmation with the report
 * number. Only the number is kept from the 201; the run is unchanged, so
 * `onRunChanged` is never called and nothing is written to the query cache.
 *
 * Closing (Cancel, Esc, the confirmation's close) navigates back to the run
 * with `replace`, which unmounts the form and with it the rejection text.
 * There is no trigger to restore focus to, so focus goes to the Results
 * report link, or else the page h1, never the body (FR-027).
 *
 * The modal is droppable (R7): the catalog does not depend on this file.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { copyText } from '@/lib/provisional-copy'

import { RejectionConfirmation } from './report/RejectionConfirmation'
import { NOT_TRANSMISSIBLE, REJECTION_OVERRIDES, RejectionForm } from './report/RejectionForm'
import type { RunScreenProps } from './types'

/** After the modal unmounts: the report link on Results, or the page h1. */
function restoreFocus(event: Event) {
  event.preventDefault()
  const target =
    document.querySelector<HTMLElement>('a[href$="/report-rejection"]') ?? document.querySelector<HTMLElement>('h1')
  // mission 6 WP13: RunHeader's h1 carries tabIndex={-1}; no DOM write here.
  target?.focus()
}

export default function ReportRejection({ run }: RunScreenProps) {
  const navigate = useNavigate()
  const [reportNumber, setReportNumber] = useState<string | null>(null)

  function close() {
    setReportNumber(null)
    navigate(`/app/runs/${encodeURIComponent(run.run_id)}`, { replace: true })
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close()
      }}
    >
      <DialogContent size="md" onCloseAutoFocus={restoreFocus}>
        <DialogHeader>
          <DialogTitle>Report a bureau rejection</DialogTitle>
        </DialogHeader>
        {run.path === 'analyze' ? (
          <>
            <DialogBody>
              <ProblemBand problem={NOT_TRANSMISSIBLE} override={REJECTION_OVERRIDES} />
            </DialogBody>
            <DialogFooter>
              <DialogClose asChild>
                <Button>{copyText('confirmationClose')}</Button>
              </DialogClose>
            </DialogFooter>
          </>
        ) : reportNumber !== null ? (
          <RejectionConfirmation reportNumber={reportNumber} runId={run.run_id} onClose={close} />
        ) : (
          <RejectionForm run={run} onSubmitted={setReportNumber} />
        )}
      </DialogContent>
    </Dialog>
  )
}
